import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Shield, Building2, CalendarRange, Wrench, Sparkles, Brush, Sofa, Droplets, ClipboardList, Users, ShieldCheck, Car, TrafficCone, KeyRound, Gem } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { services } from "../data/site";

const heroTextByService = {
  housekeeping:
    "At STARLINK Security & Facilities Pvt. Ltd. we understand that a clean environment is the foundation of a productive workspace and a healthy lifestyle. Extending our core values of discipline and vigilance, we provide comprehensive Housekeeping Services designed to maintain your premises at the highest standards of hygiene and presentation.",
  valet:
    "STARLINK Security & Facilities Pvt. Ltd. goes beyond traditional guarding to offer premium Valet Parking services. We understand that the guest experience begins the moment they arrive. Our valet service combines the hospitality of a warm welcome with the vigilance and trust inherent in our security background.",
  events:
    "At STARLINK Security Force, we understand that a successful event is defined not just by the program on stage, but by the safety, comfort, and fluidity of the guest experience. Moving beyond traditional guarding, STARLINK offers a holistic Event Management Ecosystem. We provide a single point of accountability for critical logistical support, ensuring your event runs seamlessly from the parking lot to the VIP lounge.",
};

const DEFAULT_HERO_TEXT = "Secure your world with STARLINK Security & Facilities Pvt. Ltd.";

const bodyTextByService = {
  housekeeping:
    "We move beyond basic cleaning to offer total facility upkeep. Our staff is not only trained in modern cleaning techniques but is also background-verified and disciplined, ensuring that your property is treated with care and respect",
  valet:
    "Trust. When you hand over your keys to STARLINK Security Force, you are not just hiring a driver; you are hiring a disciplined professional committed to safety, punctuality, and integrity. We turn the parking lot from a liability into an asset.",
};

const DEFAULT_BODY_TEXT =
  "STARLINK Security Force is a premier provider of integrated security and facility solutions, dedicated to protecting what matters most—your people, property, and peace of mind. Built on a foundation of discipline and integrity, we deploy highly trained personnel and strategic safety protocols to create a secure environment for every client.";

const headingByService = {
  events: "Event Management Ecosystem",
  housekeeping: "Cleanliness. Hygiene. Discipline.",
  valet: "You are not just hiring a driver; you are hiring a disciplined professional committed to safety, punctuality, and integrity.",
};

const DEFAULT_HEADING = "STARLINK Security Force: Your Shield of Reliability";

const advantageByService = {
  events: {
    title: "The STARLINK Advantage",
    points: [
      {
        title: "Integrated Communication",
        text: "Because our Security, Valet, and Housekeeping teams are all under the STARLINK banner, communication is instant. If a valet notices a suspicious vehicle, security is notified immediately. If security notices a spill causing a slip hazard, housekeeping is alerted instantly.",
      },
      {
        title: "Uniformed Professionalism",
        text: "All staff are impeccably groomed, uniformed, and trained in soft skills to act as ambassadors for your event.",
      },
      {
        title: "Customized Planning",
        text: "We conduct a thorough risk assessment and site survey before every event to deploy the right mix of personnel.",
      },
    ],
  },
};

const expertiseByService = {
  security: [
    {
      icon: Shield,
      title: "Manned Guarding",
      text: "Vigilant, uniformed professionals available 24/7 to deter threats and ensure on-site safety.",
    },
    {
      icon: Building2,
      title: "Corporate & Industrial Security",
      text: "Tailored risk management solutions to safeguard business assets, employees, and infrastructure.",
    },
    {
      icon: CalendarRange,
      title: "Event Security",
      text: "Expert crowd control and safety planning to ensure your events run smoothly and securely.",
    },
    {
      icon: Wrench,
      title: "Facilities Management",
      text: "A holistic approach that combines security with essential facility upkeep for a safe and well-maintained premise.",
    },
  ],
  housekeeping: [
    {
      icon: Sparkles,
      title: "Daily Housekeeping & Deep Cleaning",
      text: "Trained housekeeping teams for everyday upkeep plus scheduled, machine-assisted deep cleaning.",
    },
    {
      icon: Brush,
      title: "Facade & Glass Cleaning",
      text: "Safe high-rise facade, glass and window cleaning using certified equipment and trained crew.",
    },
    {
      icon: Sofa,
      title: "Carpet, Sofa & Marble Care",
      text: "Scrubbing, polishing and crystallization that keeps floors, carpets and upholstery looking new.",
    },
    {
      icon: Droplets,
      title: "Washroom & Pantry Hygiene",
      text: "Sanitized, stocked and odour-free washrooms and pantries, maintained throughout the day.",
    },
  ],
  events: [
    {
      icon: ClipboardList,
      title: "Event Planning & Coordination",
      text: "End-to-end coordination — from venue layout and vendor management to on-day execution.",
    },
    {
      icon: Users,
      title: "Crowd & Entry Management",
      text: "Smooth guest flow, queue handling and gate control for gatherings of any size.",
    },
    {
      icon: ShieldCheck,
      title: "Event Security & Marshals",
      text: "Trained bouncers and marshals who keep your event safe, calm and incident-free.",
    },
    {
      icon: Sparkles,
      title: "Post-Event Cleanup",
      text: "Rapid teardown and housekeeping so the venue is spotless the moment your event ends.",
    },
  ],
  valet: [
    {
      icon: Car,
      title: "Guest Valet Service",
      text: "Courteous, uniformed valets delivering a premium first impression at hotels, malls and hospitals.",
    },
    {
      icon: TrafficCone,
      title: "Traffic & Parking Management",
      text: "Planned vehicle flow and slot management that prevents congestion, even at peak hours.",
    },
    {
      icon: KeyRound,
      title: "Key & Vehicle Safety",
      text: "Strict key-handling protocol and documented vehicle checks, so every handover is dispute-free.",
    },
    {
      icon: Gem,
      title: "Valet for Weddings & Events",
      text: "Scalable valet teams for weddings, launches and corporate events — from 50 to 5,000 guests.",
    },
  ],
};

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = services.find((s) => s.id === serviceId);
  if (!service) return <Navigate to="/" replace />;
  const others = services.filter((s) => s.id !== service.id);

  return (
    <>
      <section className="bg-white border-b border-slate-200" data-testid="service-detail-page">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 lg:py-20">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6">
                <Eyebrow>Services / Pillar {service.num}</Eyebrow>
                <h1
                  className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-slate-900"
                  data-testid="service-detail-title"
                >
                  {service.title}
                </h1>
                <p className="mt-3 text-base font-medium text-sky-700">{service.tagline}</p>
                <p className="mt-6 text-base leading-relaxed text-slate-600">{heroTextByService[service.id] || DEFAULT_HERO_TEXT}</p>
                <Link
                  to="/contact"
                  data-testid="service-detail-quote-button"
                  className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#0F2A4A] px-7 py-3.5 text-sm font-semibold text-white hover:bg-sky-700 transition-colors duration-300"
                >
                  Request a quote
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="lg:col-span-6">
                <div className="relative">
                  <div className="overflow-hidden rounded-2xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="h-72 lg:h-96 w-full object-cover"
                      data-testid="service-detail-image"
                    />
                  </div>
                  <span className="absolute -top-5 -left-3 sm:-left-5 rounded-lg bg-[#0F2A4A] px-4 py-2 font-mono text-sm text-white">
                    {service.num}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Our Core Expertise</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900">
              {headingByService[service.id] || DEFAULT_HEADING}
            </h2>
            {advantageByService[service.id] ? (
              <div className="mt-5" data-testid="service-advantage-block">
                <p className="text-base font-extrabold tracking-tight text-slate-900">
                  {advantageByService[service.id].title}
                </p>
                <div className="mt-4 space-y-4">
                  {advantageByService[service.id].points.map((p) => (
                    <p key={p.title} className="text-sm leading-relaxed text-slate-600">
                      <span className="font-semibold text-[#E5322D]">{p.title}: </span>
                      {p.text}
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                {bodyTextByService[service.id] || DEFAULT_BODY_TEXT}
              </p>
            )}
          </Reveal>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {expertiseByService[service.id].map((e, i) => (
              <Reveal key={e.title} delay={i * 0.08} className="h-full">
                <div
                  className="h-full rounded-xl border border-slate-200/80 bg-white p-6 hover:border-sky-500/40 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`service-detail-expertise-${i}`}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-[#E5322D]">
                    <e.icon className="h-4 w-4" />
                  </span>
                  <p className="mt-4 text-sm font-semibold text-slate-800">{e.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{e.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20 border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Explore more</Eyebrow>
            <div className="mt-6 grid sm:grid-cols-3 gap-5">
              {others.map((o) => (
                <Link
                  key={o.id}
                  to={`/services/${o.id}`}
                  data-testid={`service-detail-other-${o.id}`}
                  className="group rounded-xl border border-slate-200/80 bg-white p-6 hover:border-sky-500/40 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(2,132,199,0.12)] transition-all duration-300"
                >
                  <span className="font-mono text-xs text-slate-300">{o.num}</span>
                  <h3 className="mt-2 text-lg font-semibold text-slate-900 flex items-center justify-between">
                    {o.title}
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-sky-600 group-hover:translate-x-1 transition-all duration-300" />
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">{o.tagline}</p>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
